import type { ExpressCheckoutAddress } from '@stripe/stripe-js';
import usStates from '../usStates';
import { ShippingAddress } from './address';

// the purchase's shipping-name rule, as the shop applies it: a letter first, then letters, spaces and
// ,.'- and two or more
const SHIPPING_NAME = /^\p{L}+[\p{L} ,.'-]*$/u;

function isShippingName(value: string): boolean {
  return value.length >= 2 && SHIPPING_NAME.test(value);
}

/** drops the characters the shipping-name rule refuses (digits, emoji, brackets) and single-spaces */
function cleanName(value: string): string {
  return value
    .replace(/[^\p{L} ,.'-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** a wallet returns the contact as typed, "Fl" or "Florida"; the purchase wants the code */
function toStateCode(state: string): string {
  const trimmed = state.trim();
  const match = usStates.find(
    (region) => region.abbreviation === trimmed.toUpperCase() || region.name.toLowerCase() === trimmed.toLowerCase(),
  );
  return match?.abbreviation ?? trimmed;
}

/**
 * The shipping address an Apple Pay or Google Pay sheet handed back, in the shape the purchase takes.
 * Null when the sheet's contact has no usable name: apple pay leaves the billing name empty, so the
 * shipping contact's name is the one that counts, and a one-word name is used as both names because
 * the purchase refuses a last name under two letters.
 */
export function shippingAddressFromWallet(
  shipping: { name: string; address: ExpressCheckoutAddress },
  phone: string | undefined,
): ShippingAddress | null {
  const [first = '', ...rest] = cleanName(shipping.name).split(' ');
  const last = rest.join(' ');
  if (!isShippingName(first)) {
    return null;
  }
  const { address } = shipping;
  return {
    firstName: first,
    lastName: isShippingName(last) ? last : first,
    line1: address.line1.trim(),
    line2: address.line2?.trim() ?? '',
    city: address.city.trim(),
    state: toStateCode(address.state),
    zip: address.postal_code.trim(),
    country: address.country.trim().toUpperCase() || 'US',
    phone: phone ?? '',
  };
}
